// useSmsTier — resolves the SMS portal permission tier from the signed-in user.
//
// The standalone prototype had its own Firebase Google sign-in gate that
// resolved a tier (viewer | editor | publisher) from a `team` collection /
// TEAM_MEMBERS mirror. In the admin app the user is already authenticated via
// Auth0 (the /secure router guard), so the gate is dropped: we map the host's
// current user to a tier by email against the same TEAM_MEMBERS roster,
// defaulting to least privilege (viewer) for anyone not on it.
//
// DEV NOTE: tier should ultimately come from the user's Auth0 roles/claims
// rather than an email roster — this mirror is the documented seam for that.
import { computed } from "vue";
import { store } from "../../../store";
import { TEAM_MEMBERS, capitalize } from "../sms-config";

function initialsFrom(s) {
  const parts = String(s || "")
    .replace(/@.*/, "")
    .split(/[.\s_-]+/)
    .filter(Boolean);
  const ii = ((parts[0] || "")[0] || "") + ((parts[1] || "")[0] || "");
  return (ii || "U").toUpperCase().slice(0, 2);
}

export function useSmsTier() {
  const currentUser = computed(() => store.getters.currentUser);

  const email = computed(() =>
    ((currentUser.value && currentUser.value.email) || "").toLowerCase()
  );

  const member = computed(
    () => TEAM_MEMBERS.find(m => m.email.toLowerCase() === email.value) || null
  );

  const tier = computed(() => (member.value ? member.value.portal : "viewer"));
  const tierLabel = computed(() => capitalize(tier.value));

  // Viewers are read-only; editors and publishers can draft/submit.
  const canEdit = computed(
    () => tier.value === "editor" || tier.value === "publisher"
  );
  const canPublish = computed(() => tier.value === "publisher");

  // Header identity — prefer the roster record, fall back to the Auth0 profile.
  const displayName = computed(
    () =>
      (currentUser.value &&
        (currentUser.value.name || currentUser.value.email)) ||
      (member.value && member.value.name) ||
      "User"
  );
  const initials = computed(() =>
    member.value ? member.value.initials : initialsFrom(displayName.value)
  );
  const avatarClass = computed(() =>
    member.value ? member.value.avatarClass : "bg-brand-teal text-white"
  );

  return {
    tier,
    tierLabel,
    canEdit,
    canPublish,
    displayName,
    initials,
    avatarClass,
    member
  };
}
